import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, XIcon } from "./Icons";
import { SectionHeader } from "./SectionHeader";

// Custom debounce utility
const debounce = (func, wait) => {
	let timeout;
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};
		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

// MultiSelectDropdown with aggressive state isolation to prevent closing during selection
const MultiSelectDropdown = React.memo(
	({
		label,
		options = [],
		selectedValues = [],
		onToggle,
		placeholder = "Select options...",
		maxHeight = "200px",
		isLoading = false,
		error = null,
		onRefresh = null,
		showCount = true,
	}) => {
		// Use ref for dropdown state that survives all re-renders
		const dropdownStateRef = useRef({
			isOpen: false,
			isSelecting: false,
			forceRender: 0,
		});

		// Select/deselect option - AGGRESSIVE PROTECTION
		const [pendingSelections, setPendingSelections] = useState(selectedValues);

		// Force re-render function
		const [, forceUpdate] = useState(0);
		const triggerRerender = useCallback(() => {
			dropdownStateRef.current.forceRender += 1;
			forceUpdate((prev) => prev + 1);
		}, []);

		const [searchTerm, setSearchTerm] = useState("");
		const [menuPosition, setMenuPosition] = useState({
			top: 0,
			left: 0,
			width: 0,
		});
		const triggerRef = useRef(null);
		const menuRef = useRef(null);
		const selectionTimeoutRef = useRef(null);

		// Get current open state
		const isOpen = dropdownStateRef.current.isOpen;

		// Calculate menu position
		const updateMenuPosition = useCallback(() => {
			if (triggerRef.current && dropdownStateRef.current.isOpen) {
				const rect = triggerRef.current.getBoundingClientRect();
				setMenuPosition({
					top: rect.bottom + window.scrollY + 2,
					left: rect.left + window.scrollX,
					width: rect.width,
				});
			}
		}, []);

		// Update position when opened
		useEffect(() => {
			if (dropdownStateRef.current.isOpen) {
				updateMenuPosition();

				const handleResize = () => updateMenuPosition();
				const handleScroll = () => updateMenuPosition();

				window.addEventListener("resize", handleResize);
				window.addEventListener("scroll", handleScroll, true);

				return () => {
					window.removeEventListener("resize", handleResize);
					window.removeEventListener("scroll", handleScroll, true);
				};
			}
		}, [isOpen, updateMenuPosition]);

		// Close dropdown when clicking outside - but not during selection
		useEffect(() => {
			const handleClickOutside = (event) => {
				if (
					dropdownStateRef.current.isOpen &&
					!dropdownStateRef.current.isSelecting &&
					triggerRef.current &&
					!triggerRef.current.contains(event.target) &&
					menuRef.current &&
					!menuRef.current.contains(event.target)
				) {
					console.log("Closing dropdown - clicked outside");
					dropdownStateRef.current.isOpen = false;
					setSearchTerm("");
					triggerRerender();
				}
			};

			if (dropdownStateRef.current.isOpen) {
				// Delay adding the listener to prevent immediate closure
				const timeoutId = setTimeout(() => {
					document.addEventListener("mousedown", handleClickOutside);
				}, 100);

				return () => {
					clearTimeout(timeoutId);
					document.removeEventListener("mousedown", handleClickOutside);
				};
			}
		}, [isOpen, triggerRerender]);

		// Handle Escape key
		useEffect(() => {
			const handleKeyDown = (event) => {
				if (event.key === "Escape" && dropdownStateRef.current.isOpen) {
					console.log("Closing dropdown - Escape key");
					dropdownStateRef.current.isOpen = false;
					dropdownStateRef.current.isSelecting = false;
					setSearchTerm("");
					triggerRerender();
				}
			};

			if (dropdownStateRef.current.isOpen) {
				document.addEventListener("keydown", handleKeyDown);
				return () => document.removeEventListener("keydown", handleKeyDown);
			}
		}, [isOpen, triggerRerender]);

		useEffect(() => {
			setPendingSelections(selectedValues);
		}, [selectedValues]);

		// Filter options based on search term
		const filteredOptions = useMemo(() => {
			if (!searchTerm.trim()) return options;
			const term = searchTerm.toLowerCase();
			return options.filter(
				(option) => option && option.toString().toLowerCase().includes(term),
			);
		}, [options, searchTerm]);

		// Toggle dropdown open/closed
		const handleTriggerClick = useCallback(
			(e) => {
				e.preventDefault();
				e.stopPropagation();

				if (!isLoading) {
					console.log("Trigger clicked - toggling dropdown");
					dropdownStateRef.current.isOpen = !dropdownStateRef.current.isOpen;
					dropdownStateRef.current.isSelecting = false;

					if (dropdownStateRef.current.isOpen) {
						setSearchTerm("");
						updateMenuPosition();
					}

					triggerRerender();
				}
			},
			[isLoading, updateMenuPosition, triggerRerender],
		);

		const handleToggleOption = useCallback((option, event) => {
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}

			// Update local state only - no parent notification
			setPendingSelections((prev) => {
				if (prev.includes(option)) {
					return prev.filter((item) => item !== option);
				} else {
					return [...prev, option];
				}
			});
		}, []);

		const handleDoneClick = useCallback(
			(e) => {
				e.preventDefault();
				e.stopPropagation();

				// NOW send all changes to parent at once
				const addedItems = pendingSelections.filter(
					(item) => !selectedValues.includes(item),
				);
				const removedItems = selectedValues.filter(
					(item) => !pendingSelections.includes(item),
				);

				// Call onToggle for each changed item
				[...addedItems, ...removedItems].forEach((item) => {
					onToggle(item);
				});

				// Close dropdown
				dropdownStateRef.current.isOpen = false;
				setSearchTerm("");
				triggerRerender();
			},
			[pendingSelections, selectedValues, onToggle, triggerRerender],
		);

		// Handle search input
		const handleSearchChange = useCallback((e) => {
			setSearchTerm(e.target.value);
		}, []);

		// Cleanup timeouts on unmount
		useEffect(() => {
			return () => {
				if (selectionTimeoutRef.current) {
					clearTimeout(selectionTimeoutRef.current);
				}
			};
		}, []);

		const selectedCount = selectedValues.length;
		const displayText =
			selectedCount > 0 ? `${selectedCount} selected` : placeholder;

		return (
			<div className="multi-select-container">
				<label
					style={{
						fontSize: "11px",
						fontWeight: "600",
						marginBottom: "4px",
						display: "block",
						color: "#374151",
					}}
				>
					{label}{" "}
					{showCount && selectedCount > 0 && `(${selectedCount} selected)`}
				</label>

				<div className="multi-select-dropdown">
					<button
						ref={triggerRef}
						type="button"
						className={`multi-select-trigger ${isOpen ? "open" : ""} ${isLoading ? "loading" : ""}`}
						onClick={handleTriggerClick}
						disabled={isLoading}
						style={{
							width: "100%",
							padding: "6px 8px",
							border: "1px solid #d1d5db",
							borderRadius: "4px",
							background: "white",
							fontSize: "11px",
							cursor: isLoading ? "not-allowed" : "pointer",
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							transition: "all 0.2s",
							boxSizing: "border-box",
							opacity: isLoading ? 0.6 : 1,
							borderColor: isOpen ? "#0076BD" : "#d1d5db",
							boxShadow: isOpen ? "0 0 0 2px rgba(0, 118, 189, 0.2)" : "none",
						}}
					>
						<span
							style={{
								color: "#374151",
								flex: 1,
								textAlign: "left",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{displayText}
						</span>
						<div
							style={{
								transition: "transform 0.2s ease",
								color: "#6b7280",
								marginLeft: "8px",
								flexShrink: 0,
								transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
							}}
						>
							▼
						</div>
					</button>

					{isOpen &&
						createPortal(
							<div
								ref={menuRef}
								className="multi-select-menu"
								style={{
									position: "absolute",
									top: `${menuPosition.top}px`,
									left: `${menuPosition.left}px`,
									width: `${menuPosition.width}px`,
									background: "white",
									border: "1px solid #d1d5db",
									borderRadius: "4px",
									boxShadow:
										"0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05)",
									zIndex: 9999,
									overflow: "hidden",
									maxHeight: "300px",
								}}
							>
								{/* Search input */}
								<div
									className="multi-select-search"
									style={{
										padding: "8px",
										borderBottom: "1px solid #e5e7eb",
										background: "#f8f9fa",
									}}
								>
									<input
										type="text"
										placeholder="Search options..."
										value={searchTerm}
										onChange={handleSearchChange}
										style={{
											width: "100%",
											padding: "6px 8px",
											border: "1px solid #d1d5db",
											borderRadius: "3px",
											fontSize: "11px",
											outline: "none",
											boxSizing: "border-box",
											background: "white",
										}}
										autoFocus
									/>
								</div>

								{/* Loading state */}
								{isLoading && (
									<div
										style={{
											textAlign: "center",
											padding: "12px",
											fontSize: "11px",
											color: "#6b7280",
										}}
									>
										Loading options...
									</div>
								)}

								{/* Error state */}
								{error && (
									<div
										style={{
											textAlign: "center",
											padding: "12px",
											fontSize: "11px",
											color: "#dc2626",
											backgroundColor: "#fef2f2",
											borderRadius: "4px",
											margin: "4px",
										}}
									>
										{error}
										{onRefresh && (
											<button
												onClick={(e) => {
													e.stopPropagation();
													onRefresh();
												}}
												style={{
													display: "block",
													margin: "4px auto 0",
													padding: "2px 6px",
													fontSize: "10px",
													background: "#dc2626",
													color: "white",
													border: "none",
													borderRadius: "3px",
													cursor: "pointer",
												}}
											>
												Retry
											</button>
										)}
									</div>
								)}

								{/* Options list */}
								{!isLoading && !error && (
									<div style={{ maxHeight, overflowY: "auto" }}>
										{filteredOptions.length > 0 ? (
											<div>
												{filteredOptions.map((option, index) => {
													const isSelected = pendingSelections.includes(option);

													return (
														<div
															key={`${option}-${index}`}
															className="multi-select-option"
															style={{
																display: "flex",
																alignItems: "center",
																padding: "8px 12px",
																cursor: "pointer",
																fontSize: "11px",
																borderBottom:
																	index < filteredOptions.length - 1
																		? "1px solid #f3f4f6"
																		: "none",
																transition: "background-color 0.15s",
																backgroundColor: isSelected
																	? "#eff6ff"
																	: "transparent",
																color: isSelected ? "#0076BD" : "inherit",
															}}
															onMouseDown={(e) => {
																e.preventDefault();
																e.stopPropagation();
																handleToggleOption(option, e);
															}}
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
															}}
															onMouseEnter={(e) => {
																e.target.style.backgroundColor = isSelected
																	? "#dbeafe"
																	: "#f8fafc";
															}}
															onMouseLeave={(e) => {
																e.target.style.backgroundColor = isSelected
																	? "#eff6ff"
																	: "transparent";
															}}
														>
															<input
																type="checkbox"
																checked={isSelected}
																onChange={() => {}}
																style={{
																	marginRight: "8px",
																	width: "14px",
																	height: "14px",
																	flexShrink: 0,
																	pointerEvents: "none",
																}}
																readOnly
															/>
															<span
																style={{
																	flex: 1,
																	color: "inherit",
																	whiteSpace: "nowrap",
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																}}
															>
																{option}
															</span>
														</div>
													);
												})}
											</div>
										) : (
											<div
												style={{
													textAlign: "center",
													padding: "12px",
													fontSize: "11px",
													color: "#6b7280",
												}}
											>
												{searchTerm
													? "No matching options"
													: "No options available"}
											</div>
										)}
									</div>
								)}

								{/* Done button section with instructional note */}
								<div
									style={{
										padding: "8px",
										borderTop: "1px solid #e5e7eb",
										textAlign: "right",
										backgroundColor: "#f8f9fa",
									}}
								>
									{/* Small instruction note */}
									<div
										style={{
											fontSize: "9px",
											color: "#6b7280",
											marginBottom: "6px",
											textAlign: "center",
											fontStyle: "italic",
										}}
									>
										Press "Done" to save selections
									</div>

									<button
										onClick={handleDoneClick}
										style={{
											background: "#0076BD",
											color: "white",
											border: "none",
											borderRadius: "4px",
											padding: "6px 12px",
											fontSize: "11px",
											cursor: "pointer",
											fontWeight: "500",
											transition: "background-color 0.2s",
										}}
										onMouseEnter={(e) => {
											e.target.style.backgroundColor = "#0056b3";
										}}
										onMouseLeave={(e) => {
											e.target.style.backgroundColor = "#0076BD";
										}}
									>
										Done
									</button>
								</div>
							</div>,
							document.body,
						)}
				</div>

				<style jsx>{`
        .multi-select-container {
          position: relative;
          margin-bottom: 12px;
        }
      `}</style>
			</div>
		);
	},
);

const ThresholdInput = React.memo(
	({ field, label, hint, placeholder, value, defaultValue, onChange }) => {
		const [localValue, setLocalValue] = useState("");
		const [isFocused, setIsFocused] = useState(false);

		// Helper function to format large numbers for display
		const formatNumber = (num) => {
			if (num >= 1000000000000) {
				return (num / 1000000000000).toFixed(1) + "T";
			} else if (num >= 1000000000) {
				return (num / 1000000000).toFixed(1) + "B";
			} else if (num >= 1000000) {
				return (num / 1000000).toFixed(1) + "M";
			} else if (num >= 1000) {
				return (num / 1000).toFixed(1) + "K";
			}
			return num.toString();
		};

		// Helper function to parse input values
		const parseInputValue = (inputStr) => {
			if (!inputStr || inputStr === "") {
				return defaultValue;
			}

			const str = inputStr.toString().toLowerCase();
			let multiplier = 1;
			let baseValue = str;

			if (str.includes("t")) {
				multiplier = 1000000000000;
				baseValue = str.replace("t", "");
			} else if (str.includes("b")) {
				multiplier = 1000000000;
				baseValue = str.replace("b", "");
			} else if (str.includes("m")) {
				multiplier = 1000000;
				baseValue = str.replace("m", "");
			} else if (str.includes("k")) {
				multiplier = 1000;
				baseValue = str.replace("k", "");
			}

			const parsed = parseFloat(baseValue);
			return isNaN(parsed) ? defaultValue : parsed * multiplier;
		};

		// Update local value when prop value changes (but not when focused)
		useEffect(() => {
			if (!isFocused) {
				setLocalValue(formatNumber(value || defaultValue));
			}
		}, [value, defaultValue, isFocused]);

		const handleChange = useCallback((e) => {
			setLocalValue(e.target.value);
		}, []);

		const handleFocus = useCallback(
			(e) => {
				setIsFocused(true);
				// Show raw number when focused
				setLocalValue((value || defaultValue).toString());
			},
			[value, defaultValue],
		);

		const handleBlur = useCallback(() => {
			setIsFocused(false);
			const parsedValue = parseInputValue(localValue);

			// Update parent if value changed
			if (parsedValue !== value) {
				onChange(field, parsedValue);
			}

			// Format the display value
			setLocalValue(formatNumber(parsedValue));
		}, [localValue, value, field, onChange]);

		const handleKeyPress = useCallback((e) => {
			if (e.key === "Enter") {
				e.target.blur(); // This will trigger handleBlur
			}
		}, []);

		return (
			<div className="threshold-input-group">
				<label className="threshold-input-label">
					{label}
					<span className="threshold-hint">({hint})</span>
				</label>
				<div className="threshold-input-wrapper">
					<input
						type="text"
						placeholder={placeholder}
						className="threshold-input"
						value={localValue}
						onChange={handleChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onKeyPress={handleKeyPress}
					/>
					<span className="threshold-display">
						Current: {formatNumber(value || defaultValue)}
					</span>
				</div>
			</div>
		);
	},
);

// Custom debounce hook
const useDebounce = (value, delay) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => clearTimeout(handler);
	}, [value, delay]);

	return debouncedValue;
};

const BurgerIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="3" y1="6" x2="21" y2="6"></line>
		<line x1="3" y1="12" x2="21" y2="12"></line>
		<line x1="3" y1="18" x2="21" y2="18"></line>
	</svg>
);

const UploadIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17,8 12,3 7,8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>
	</svg>
);

const RemoveIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

const RefreshIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="23 4 23 10 17 10"></polyline>
		<polyline points="1 20 1 14 7 14"></polyline>
		<path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
	</svg>
);

// Memoized SearchInput component with debouncing to maintain focus
const SearchInput = React.memo(
	({
		value,
		onChange,
		onEnterPress,
		placeholder,
		className,
		autoComplete = "off",
		style,
		onClear,
		enterKeyAction = false,
	}) => {
		const inputRef = useRef(null);
		const onEnterRef = useRef(onEnterPress);
		const [localValue, setLocalValue] = useState(value || "");

		// Update refs when props change
		useEffect(() => {
			onEnterRef.current = onEnterPress;
		}, [onEnterPress]);

		// Update local value when prop value changes (but only if input is not focused)
		useEffect(() => {
			if (inputRef.current && !inputRef.current.matches(":focus")) {
				setLocalValue(value || "");
			}
		}, [value]);

		// Handle input changes WITHOUT triggering parent updates
		const handleInput = useCallback((e) => {
			const newValue = e.target.value;
			setLocalValue(newValue); // Only update local state - NO parent updates
		}, []);

		// Only trigger parent callbacks on Enter key
		const handleKeyPress = useCallback(
			(e) => {
				if (e.key === "Enter") {
					e.preventDefault();

					if (enterKeyAction && localValue.trim() && onEnterRef.current) {
						// Call the enter handler (adds tag)
						onEnterRef.current(localValue.trim());

						// Clear the input after Enter
						setLocalValue("");
					}
				}
			},
			[enterKeyAction, localValue],
		);

		// Handle blur - only update parent if value actually changed
		const handleBlur = useCallback(() => {
			// Only call onChange if the value is different from the prop
			if (onChange && localValue !== value) {
				const mockEvent = {
					target: { value: localValue },
					preventDefault: () => {},
					stopPropagation: () => {},
				};
				onChange(mockEvent);
			}
		}, [onChange, localValue, value]);

		// Handle clear button
		const handleClear = useCallback(() => {
			setLocalValue("");
			if (inputRef.current) {
				inputRef.current.focus();
			}

			if (onClear) {
				onClear();
			} else if (onChange) {
				const mockEvent = {
					target: { value: "" },
					preventDefault: () => {},
					stopPropagation: () => {},
				};
				onChange(mockEvent);
			}
		}, [onClear, onChange]);

		return (
			<div style={{ position: "relative", ...style }}>
				<input
					ref={inputRef}
					type="text"
					value={localValue}
					onInput={handleInput}
					onKeyPress={handleKeyPress}
					onBlur={handleBlur}
					placeholder={placeholder}
					className={className}
					autoComplete={autoComplete}
					style={{
						width: "100%",
						padding: "6px 25px 6px 8px",
						border: "1px solid #d1d5db",
						borderRadius: "4px",
						fontSize: "11px",
						outline: "none",
						backgroundColor: "white",
						color: "#333",
						caretColor: "#0076BD",
						transform: "translateZ(0)",
						willChange: "auto",
						backfaceVisibility: "visible",
						WebkitTextFillColor: "#333",
						WebkitOpacity: 1,
					}}
					onFocus={(e) => {
						e.target.style.borderColor = "#0076BD";
						e.target.style.boxShadow = "0 0 0 2px rgba(0, 118, 189, 0.2)";
						e.target.style.caretColor = "#0076BD";
					}}
				/>

				{enterKeyAction && (
					<div
						style={{
							position: "absolute",
							right: "30px",
							top: "50%",
							transform: "translateY(-50%)",
							fontSize: "9px",
							color: "#9ca3af",
							pointerEvents: "none",
							opacity: localValue.trim() ? 1 : 0.6,
						}}
					>
						↵
					</div>
				)}

				{localValue && (onClear || onChange) && (
					<button
						onClick={handleClear}
						className="clear-search-btn"
						type="button"
						style={{
							position: "absolute",
							right: "6px",
							top: "50%",
							transform: "translateY(-50%)",
							background: "none",
							border: "none",
							color: "#6b7280",
							cursor: "pointer",
							fontSize: "16px",
							padding: "2px",
						}}
					>
						×
					</button>
				)}
			</div>
		);
	},
);

const SearchTagsDisplay = React.memo(
	({ activeSearchTags, onRemoveTag, style }) => {
		const hasAnyTags =
			activeSearchTags?.audienceIds?.length > 0 ||
			activeSearchTags?.providers?.length > 0;

		if (!hasAnyTags) {
			return null;
		}

		const tagTypeConfig = {
			audienceIds: {
				label: "Audience ID",
				color: "#059669",
				bgColor: "#ecfdf5",
			},
			providers: { label: "Provider", color: "#dc2626", bgColor: "#fef2f2" },
		};

		return (
			<div
				style={{
					padding: "12px",
					borderBottom: "1px solid #e5e7eb",
					backgroundColor: "#f9fafb",
					...style,
				}}
			>
				<div
					style={{
						fontSize: "11px",
						fontWeight: "600",
						color: "#374151",
						marginBottom: "8px",
					}}
				>
					Active Search Filters
				</div>

				<div
					style={{
						display: "flex",
						flexDirection: "column",
						gap: "6px",
					}}
				>
					{Object.keys(tagTypeConfig).map((tagKey) => {
						const tags = activeSearchTags[tagKey] || [];
						if (tags.length === 0) return null;

						const config = tagTypeConfig[tagKey];

						return (
							<div
								key={tagKey}
								style={{
									display: "flex",
									flexWrap: "wrap",
									gap: "4px",
									alignItems: "center",
								}}
							>
								<span
									style={{
										fontSize: "9px",
										fontWeight: "500",
										color: "#6b7280",
										minWidth: "60px",
									}}
								>
									{config.label}:
								</span>

								<div
									style={{
										display: "flex",
										flexWrap: "wrap",
										gap: "4px",
										flex: 1,
									}}
								>
									{tags.map((tag, index) => (
										<span
											key={`${tagKey}-${index}`}
											style={{
												fontSize: "9px",
												padding: "2px 6px",
												borderRadius: "12px",
												backgroundColor: config.bgColor,
												color: config.color,
												border: `1px solid ${config.color}`,
												fontWeight: "500",
												display: "flex",
												alignItems: "center",
												gap: "4px",
												maxWidth: "150px",
											}}
										>
											<span
												style={{
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{tag}
											</span>
											<button
												onClick={() => onRemoveTag(tagKey, tag)}
												style={{
													background: "none",
													border: "none",
													color: config.color,
													cursor: "pointer",
													fontSize: "12px",
													padding: "0",
													margin: "0",
													lineHeight: "1",
													borderRadius: "2px",
													width: "12px",
													height: "12px",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
												title={`Remove ${tag}`}
											>
												×
											</button>
										</span>
									))}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	},
);

// Memoized LimitInput component
const LimitInput = React.memo(({ value, onChange }) => {
	const [localValue, setLocalValue] = useState(value || 100);

	useEffect(() => {
		setLocalValue(value || 100);
	}, [value]);

	const handleChange = useCallback((e) => {
		const newValue = e.target.value;
		setLocalValue(newValue);
	}, []);

	const handleBlur = useCallback(() => {
		const numValue = parseInt(localValue, 10);
		const finalValue = isNaN(numValue) || numValue < 0 ? 200 : numValue;
		if (finalValue !== value) {
			onChange(finalValue);
		}
	}, [localValue, value, onChange]);

	const handleKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				handleBlur();
			}
		},
		[handleBlur],
	);

	return (
		<input
			type="number"
			value={localValue}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyPress={handleKeyPress}
			placeholder="200"
			min="0"
			max="100000"
			style={{
				width: "100%",
				padding: "6px 8px",
				border: "1px solid #d1d5db",
				borderRadius: "4px",
				fontSize: "11px",
				backgroundColor: "white",
				color: "#374151",
				boxSizing: "border-box",
			}}
		/>
	);
});

// Checkbox component for boolean values
const CheckboxField = React.memo(
	({ label, checked, onChange, disabled = false }) => {
		return (
			<div style={{ marginBottom: "12px" }}>
				<label
					style={{
						display: "flex",
						alignItems: "center",
						fontSize: "11px",
						fontWeight: "600",
						cursor: disabled ? "not-allowed" : "pointer",
						opacity: disabled ? 0.6 : 1,
					}}
				>
					<input
						type="checkbox"
						checked={checked || false}
						onChange={(e) => onChange(e.target.checked)}
						disabled={disabled}
						style={{
							marginRight: "8px",
							width: "14px",
							height: "14px",
							cursor: disabled ? "not-allowed" : "pointer",
						}}
					/>
					{label}
				</label>
			</div>
		);
	},
);

export const AudienceFiltersSidebar = React.memo(
	({
		showFilters,
		onClose,

		// ✅ FIXED: Use the combined hook directly
		filters,
		expandedSections,
		expandedCategories,
		availableOptions,
		filterValues,
		filterValuesLoading,
		filterValuesError,
		uploadStates,
		showUploadTooltip,
		hasActiveFilters,
		totalActiveFilters,
		filtersContentRef,

		// ✅ FIXED: Functions from combined hook
		updateFilter,
		toggleArrayFilter,
		clearAllFilters,
		toggleSection,
		toggleCategory,
		loadFilterValues,
		setShowUploadTooltip,
		handleFileUpload,
		clearUploadedList,
		handleSearchInputChange,
		handleSearchInputEnter,
		handleRemoveSearchTag,

		// ✅ FIXED: NO onSearch prop needed - search is handled by AudienceSearchInterface only
	}) => {
		// Add state for nested collapsible sections within Scale and Performance
		const [scalePerformanceSubSections, setScalePerformanceSubSections] =
			useState({
				reach: true,
				financial: false,
				winrates: false,
			});

		const toggleScalePerformanceSubSection = useCallback((section) => {
			setScalePerformanceSubSections((prev) => ({
				...prev,
				[section]: !prev[section],
			}));
		}, []);

		// ✅ FIXED: No search handling in sidebar - just manage tooltip state
		const handleShowUploadTooltip = useCallback(() => {
			setShowUploadTooltip(true);
		}, [setShowUploadTooltip]);

		const handleHideUploadTooltip = useCallback(() => {
			setShowUploadTooltip(false);
		}, [setShowUploadTooltip]);

		// Handle refresh for filter values
		const handleRefreshFilterValues = useCallback(() => {
			loadFilterValues(true); // Force refresh
		}, [loadFilterValues]);

		// NOW it's safe to do early returns after all hooks are called
		if (!showFilters) return null;

		const CategoryHeader = ({ title, isExpanded, onClick, children }) => (
			<div className="filter-category-box">
				<div
					className={`category-header ${isExpanded ? "expanded" : "collapsed"}`}
					onClick={onClick}
				>
					<h3 className="category-title-new">{title}</h3>
					<div
						className={`category-chevron ${isExpanded ? "expanded" : "collapsed"}`}
					>
						<ChevronDownIcon />
					</div>
				</div>
				{isExpanded && (
					<div
						className="category-content"
						style={{ paddingLeft: "8px", paddingRight: "8px" }}
					>
						{children}
					</div>
				)}
			</div>
		);

		const SubSectionHeader = ({ title, isExpanded, onClick, children }) => (
			<div className="sub-section-box" style={{ marginBottom: "12px" }}>
				<div
					className={`sub-section-header ${isExpanded ? "expanded" : "collapsed"}`}
					onClick={onClick}
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "8px 12px",
						backgroundColor: "#f8fafc",
						border: "1px solid #e2e8f0",
						borderRadius: "4px",
						cursor: "pointer",
						fontSize: "11px",
						fontWeight: "600",
						color: "#475569",
					}}
				>
					<span>{title}</span>
					<div
						className={`category-chevron ${isExpanded ? "expanded" : "collapsed"}`}
						style={{ fontSize: "12px" }}
					>
						<ChevronDownIcon />
					</div>
				</div>
				{isExpanded && <div style={{ padding: "12px 0" }}>{children}</div>}
			</div>
		);

		return (
			<div className="filters-sidebar">
				<div className="filters-header">
					<h2 className="filters-title" style={{ fontSize: "16px" }}>
						Audience Filters
					</h2>
					<div className="filters-actions">
						<button
							className="btn-medium-primary"
							onClick={clearAllFilters}
							style={{ fontSize: "12px" }}
						>
							Clear All
						</button>
						<button onClick={onClose} className="close-btn">
							<BurgerIcon />
						</button>
					</div>
				</div>

				{/* Search Tags Display */}
				<SearchTagsDisplay
					activeSearchTags={filters.activeSearchTags}
					onRemoveTag={handleRemoveSearchTag}
				/>

				<div className="filters-content" ref={filtersContentRef}>
					{/* 1. Source and Provider */}
					<CategoryHeader
						title="Source and Provider"
						isExpanded={expandedCategories.sourceProvider}
						onClick={() => toggleCategory("sourceProvider")}
					>
						<div className="filter-section">
							<div className="section-content" style={{ padding: "12px 0" }}>
								{/* Account Name Search - Enhanced */}
								<div style={{ marginBottom: "12px" }}>
									<MultiSelectDropdown
										label="Account Name"
										options={availableOptions.accountNameOptions || []}
										selectedValues={
											Array.isArray(filters.accountName)
												? filters.accountName
												: []
										}
										onToggle={(value) =>
											toggleArrayFilter("accountName", value)
										}
										placeholder="Select account names..."
										isLoading={filterValuesLoading}
										error={filterValuesError}
										onRefresh={handleRefreshFilterValues}
									/>
								</div>

								{/* Provider Name (Direct Audience Provider) - ✅ NOW DROPDOWN */}
								<div style={{ marginBottom: "12px" }}>
									<MultiSelectDropdown
										label="Data Provider"
										options={
											availableOptions.directAudienceProviderOptions || []
										}
										selectedValues={
											Array.isArray(filters.directAudienceProvider)
												? filters.directAudienceProvider
												: []
										}
										onToggle={(value) =>
											toggleArrayFilter("directAudienceProvider", value)
										}
										placeholder="Select data providers..."
										isLoading={filterValuesLoading}
										error={filterValuesError}
										onRefresh={handleRefreshFilterValues}
									/>
								</div>

								{/* ✅ NEW: Data Source Dropdown */}
								<div style={{ marginBottom: "12px" }}>
									<MultiSelectDropdown
										label="Data Source Categories"
										options={availableOptions.dataSourceOptions || []}
										selectedValues={filters.dataSource || []}
										onToggle={(value) => toggleArrayFilter("dataSource", value)}
										placeholder="Select data source categories..."
										isLoading={filterValuesLoading}
										error={filterValuesError}
										onRefresh={handleRefreshFilterValues}
									/>
								</div>

								{/* ✅ NEW: Region Dropdown */}
								<div style={{ marginBottom: "12px" }}>
									<MultiSelectDropdown
										label="Region"
										options={availableOptions.regionOptions || []}
										selectedValues={filters.region || []}
										onToggle={(value) => toggleArrayFilter("region", value)}
										placeholder="Select regions..."
										isLoading={filterValuesLoading}
										error={filterValuesError}
										onRefresh={handleRefreshFilterValues}
									/>
								</div>

								{/* Publisher Account ID Search */}
								<div style={{ marginBottom: "12px" }}>
									<label
										style={{
											fontSize: "11px",
											fontWeight: "600",
											marginBottom: "4px",
											display: "block",
										}}
									>
										Publisher Account ID (Press Enter to add)
									</label>
									<SearchInput
										value={filters.publisherAccountId || ""}
										onChange={(e) =>
											handleSearchInputChange(
												"publisherAccountIdSearch",
												e.target.value,
											)
										}
										onEnterPress={(value) =>
											handleSearchInputEnter("publisherAccountIdSearch", value)
										}
										placeholder="Type publisher account ID and press Enter..."
										enterKeyAction={true}
									/>
								</div>
							</div>
						</div>
					</CategoryHeader>

					{/* 2. Audience Attributes */}
					<CategoryHeader
						title="Audience Attributes"
						isExpanded={expandedCategories.audienceAttributes}
						onClick={() => toggleCategory("audienceAttributes")}
					>
						<div className="section-content" style={{ padding: "12px 0" }}>
							{/* Shareable OpenX Audience - Checkbox */}
							<CheckboxField
								label="Shareable OpenX Audience"
								checked={
									Array.isArray(filters?.shareable_openx_audience) &&
									filters.shareable_openx_audience.includes(true)
								}
								onChange={(checked) => {
									if (checked) {
										updateFilter("shareable_openx_audience", [true]);
									} else {
										updateFilter("shareable_openx_audience", []);
									}
								}}
							/>

							{/* Status - ✅ NOW DROPDOWN */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Status"
									options={availableOptions.statuses || []}
									selectedValues={filters.status || []}
									onToggle={(value) => toggleArrayFilter("status", value)}
									placeholder="Select statuses..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>

							{/* Type - ✅ CHANGED: Now dropdown from API */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Type"
									options={availableOptions.typeOptions || []}
									selectedValues={filters.type || []}
									onToggle={(value) => toggleArrayFilter("type", value)}
									placeholder="Select types..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>

							{/* Pricing - ✅ NOW DROPDOWN */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Pricing"
									options={availableOptions.pricingOptions || []}
									selectedValues={filters.pricing || []}
									onToggle={(value) => toggleArrayFilter("pricing", value)}
									placeholder="Select pricing options..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>
						</div>
					</CategoryHeader>

					{/* 3. Audience Descriptors */}
					<CategoryHeader
						title="Audience Descriptors"
						isExpanded={expandedCategories.audienceDescriptors}
						onClick={() => toggleCategory("audienceDescriptors")}
					>
						<div className="section-content" style={{ padding: "12px 0" }}>
							{/* ✅ NEW: Entity (from API) */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Entity"
									options={availableOptions.entityOptions || []}
									selectedValues={filters.entity || []}
									onToggle={(value) => toggleArrayFilter("entity", value)}
									placeholder="Select entities..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>

							{/* ✅ NEW: Event (from API) */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Event"
									options={availableOptions.eventOptions || []}
									selectedValues={filters.event || []}
									onToggle={(value) => toggleArrayFilter("event", value)}
									placeholder="Select events..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>

							{/* Primary Persona - ✅ NOW DROPDOWN */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Primary Persona"
									options={availableOptions.primaryPersonaOptions || []}
									selectedValues={filters.primaryPersona || []}
									onToggle={(value) =>
										toggleArrayFilter("primaryPersona", value)
									}
									placeholder="Select primary personas..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>

							{/* Audience Strategy - ✅ NOW DROPDOWN */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Audience Strategy"
									options={availableOptions.audienceStrategyOptions || []}
									selectedValues={filters.audienceStrategy || []}
									onToggle={(value) =>
										toggleArrayFilter("audienceStrategy", value)
									}
									placeholder="Select audience strategies..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>

							{/* Created With - ✅ NOW DROPDOWN */}
							<div style={{ marginBottom: "12px" }}>
								<MultiSelectDropdown
									label="Created With"
									options={availableOptions.createdWithOptions || []}
									selectedValues={filters.createdWith || []}
									onToggle={(value) => toggleArrayFilter("createdWith", value)}
									placeholder="Select creation methods..."
									isLoading={filterValuesLoading}
									error={filterValuesError}
									onRefresh={handleRefreshFilterValues}
								/>
							</div>
						</div>
					</CategoryHeader>

					{/* 4. Scale and Performance */}
					<CategoryHeader
						title="Scale and Performance"
						isExpanded={expandedCategories.scalePerformance}
						onClick={() => toggleCategory("scalePerformance")}
					>
						<div className="section-content" style={{ padding: "12px 0" }}>
							{/* Reach & Engagement Metrics Sub-section */}
							<SubSectionHeader
								title="Reach & Engagement Metrics"
								isExpanded={scalePerformanceSubSections.reach}
								onClick={() => toggleScalePerformanceSubSection("reach")}
							>
								<div className="threshold-inputs-group">
									<ThresholdInput
										field="reach"
										label="Min. Reach"
										hint="e.g., 1M, 500K, 10000"
										placeholder="Enter minimum reach..."
										value={filters?.reach ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="estimated_reach"
										label="Min. Estimated Reach"
										hint="e.g., 2M, 750K, 25000"
										placeholder="Enter minimum estimated reach..."
										value={filters?.estimated_reach ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="addressable_reach"
										label="Min. Addressable Reach"
										hint="e.g., 1.5M, 600K, 15000"
										placeholder="Enter minimum addressable reach..."
										value={filters?.addressable_reach ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="CPRMarketRequests"
										label="Min. CPR Market Requests"
										hint="e.g., 5M, 1.2M, 50000"
										placeholder="Enter minimum CPR market requests..."
										value={filters?.CPRMarketRequests ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="Impressions"
										label="Min. Impressions"
										hint="e.g., 10M, 2.5M, 100000"
										placeholder="Enter minimum impressions..."
										value={filters?.Impressions ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="ImpressionsWithClickMacro"
										label="Min. Impressions with Click Macro"
										hint="e.g., 8M, 2M, 80000"
										placeholder="Enter minimum impressions with click macro..."
										value={filters?.ImpressionsWithClickMacro ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="Clicks"
										label="Min. Clicks"
										hint="e.g., 50K, 10K, 5000"
										placeholder="Enter minimum clicks..."
										value={filters?.Clicks ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="NonZeroBids"
										label="Min. Non-Zero Bids"
										hint="e.g., 3M, 800K, 30000"
										placeholder="Enter minimum non-zero bids..."
										value={filters?.NonZeroBids ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="VTR"
										label="Min. VTR (%)"
										hint="e.g., 75, 50, 25"
										placeholder="Enter minimum VTR percentage..."
										value={filters?.VTR ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="Viewability"
										label="Min. Viewability (%)"
										hint="e.g., 85.5, 70.2, 60"
										placeholder="Enter minimum viewability percentage..."
										value={filters?.Viewability ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="ClickThroughRate"
										label="Min. Click Through Rate (%)"
										hint="e.g., 2.5, 1.25, 0.5"
										placeholder="Enter minimum CTR percentage..."
										value={filters?.ClickThroughRate ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>
								</div>
							</SubSectionHeader>

							{/* Financial Metrics Sub-section */}
							<SubSectionHeader
								title="Financial Metrics"
								isExpanded={scalePerformanceSubSections.financial}
								onClick={() => toggleScalePerformanceSubSection("financial")}
							>
								<div className="threshold-inputs-group">
									<ThresholdInput
										field="CPRMarketDemandPartnerSpendInUSD"
										label="Min. CPR Market Demand Partner Spend (USD)"
										hint="e.g., 50M, 10M, 500K"
										placeholder="Enter minimum spend..."
										value={filters?.CPRMarketDemandPartnerSpendInUSD ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="CPRMarketPublisherRevenueInUSD"
										label="Min. CPR Market Publisher Revenue (USD)"
										hint="e.g., 25M, 5M, 250K"
										placeholder="Enter minimum revenue..."
										value={filters?.CPRMarketPublisherRevenueInUSD ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="BidSpendInUSD"
										label="Min. Bid Spend (USD)"
										hint="e.g., 100M, 20M, 1M"
										placeholder="Enter minimum bid spend..."
										value={filters?.BidSpendInUSD ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="RevenuePerImpression"
										label="Min. Revenue Per Impression"
										hint="e.g., 5.50, 2.25, 1.10"
										placeholder="Enter minimum revenue per impression..."
										value={filters?.RevenuePerImpression ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="SpendPerImpression"
										label="Min. Spend Per Impression"
										hint="e.g., 4.75, 2.00, 0.95"
										placeholder="Enter minimum spend per impression..."
										value={filters?.SpendPerImpression ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="bid_cpm"
										label="Min. Bid CPM"
										hint="e.g., 15.50, 8.25, 3.75"
										placeholder="Enter minimum bid CPM..."
										value={filters?.bid_cpm ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="Bid_Guidance"
										label="Min. Bid Guidance"
										hint="e.g., 12.25, 7.50, 4.00"
										placeholder="Enter minimum bid guidance..."
										value={filters?.Bid_Guidance ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>
								</div>
							</SubSectionHeader>

							{/* Win Rate Metrics Sub-section */}
							<SubSectionHeader
								title="Win Rate Metrics"
								isExpanded={scalePerformanceSubSections.winrates}
								onClick={() => toggleScalePerformanceSubSection("winrates")}
							>
								<div className="threshold-inputs-group">
									<ThresholdInput
										field="ox_win_rate"
										label="Min. OX Win Rate (%)"
										hint="e.g., 85.5, 70.25, 50"
										placeholder="Enter minimum OX win rate..."
										value={filters?.ox_win_rate ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>

									<ThresholdInput
										field="pub_win_rate"
										label="Min. Publisher Win Rate (%)"
										hint="e.g., 75.8, 60.5, 45"
										placeholder="Enter minimum publisher win rate..."
										value={filters?.pub_win_rate ?? 0}
										defaultValue={0}
										onChange={(field, value) => updateFilter(field, value)}
									/>
								</div>
							</SubSectionHeader>
						</div>
					</CategoryHeader>

					{/* Result Limit and Upload Section */}
					<div
						style={{
							marginTop: "16px",
							padding: "12px",
							borderTop: "1px solid #e5e7eb",
							backgroundColor: "#f9fafb",
						}}
					>
						<div style={{ marginBottom: "12px" }}>
							<label
								style={{
									display: "block",
									fontSize: "11px",
									fontWeight: "500",
									color: "#374151",
									marginBottom: "4px",
								}}
							>
								Result Limit:
							</label>
							<LimitInput
								value={filters?.noOfResult || 200}
								onChange={(value) => updateFilter("noOfResult", value)}
							/>
						</div>

						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: "4px",
								margin: "0 0 8px 0",
								position: "relative",
							}}
						>
							<p
								style={{
									margin: "0",
									fontSize: "11px",
									color: "#6b7280",
									textAlign: "center",
								}}
							>
								Upload CSV files for audience ID filtering
							</p>
							<div
								style={{ position: "relative", display: "inline-flex" }}
								onMouseEnter={handleShowUploadTooltip}
								onMouseLeave={handleHideUploadTooltip}
							>
								<div
									style={{
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										width: "14px",
										height: "14px",
										borderRadius: "50%",
										backgroundColor: "#6b7280",
										color: "white",
										fontSize: "9px",
										fontWeight: "bold",
										cursor: "pointer",
									}}
								>
									i
								</div>

								{showUploadTooltip && (
									<div
										style={{
											position: "absolute",
											top: "-55px",
											left: "-60px",
											transform: "translateX(-50%)",
											background: "#333",
											color: "white",
											padding: "6px 8px",
											borderRadius: "4px",
											fontSize: "10px",
											width: "140px",
											textAlign: "center",
											lineHeight: "1.3",
											zIndex: 1000,
										}}
									>
										CSV file must contain audience ID column
									</div>
								)}
							</div>
						</div>

						<div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
							<div style={{ flex: 1 }}>
								<input
									type="file"
									accept=".csv"
									onChange={(e) =>
										handleFileUpload(e.target.files[0], "includeList")
									}
									style={{ display: "none" }}
									id="audience-include-upload"
									disabled={uploadStates.includeList.isUploading}
								/>
								<label
									htmlFor="audience-include-upload"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "4px",
										width: "100%",
										padding: "6px 8px",
										background: uploadStates.includeList.isUploading
											? "#6b7280"
											: "#0076BD",
										color: "white",
										border: "none",
										borderRadius: "4px",
										fontSize: "10px",
										fontWeight: "500",
										cursor: uploadStates.includeList.isUploading
											? "not-allowed"
											: "pointer",
										transition: "all 0.2s",
										textDecoration: "none",
										boxSizing: "border-box",
									}}
								>
									{uploadStates.includeList.isUploading ? (
										<>⏳ Uploading...</>
									) : (
										<>
											<UploadIcon />
											Include List
										</>
									)}
								</label>
							</div>

							<div style={{ flex: 1 }}>
								<input
									type="file"
									accept=".csv"
									onChange={(e) =>
										handleFileUpload(e.target.files[0], "blockedList")
									}
									style={{ display: "none" }}
									id="audience-exclude-upload"
									disabled={uploadStates.blockedList.isUploading}
								/>
								<label
									htmlFor="audience-exclude-upload"
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "4px",
										width: "100%",
										padding: "6px 8px",
										background: uploadStates.blockedList.isUploading
											? "#6b7280"
											: "#dc2626",
										color: "white",
										border: "none",
										borderRadius: "4px",
										fontSize: "10px",
										fontWeight: "500",
										cursor: uploadStates.blockedList.isUploading
											? "not-allowed"
											: "pointer",
										transition: "all 0.2s",
										textDecoration: "none",
										boxSizing: "border-box",
									}}
								>
									{uploadStates.blockedList.isUploading ? (
										<>⏳ Uploading...</>
									) : (
										<>
											<UploadIcon />
											Block List
										</>
									)}
								</label>
							</div>
						</div>

						{/* Display uploaded file names with remove buttons */}
						{(uploadStates.includeList.isUploaded ||
							uploadStates.blockedList.isUploaded) && (
							<div
								style={{
									marginBottom: "8px",
									gap: "4px",
									display: "flex",
									flexDirection: "column",
								}}
							>
								{uploadStates.includeList.isUploaded && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "4px 6px",
											backgroundColor: "#dcfce7",
											border: "1px solid #22c55e",
											borderRadius: "3px",
											fontSize: "9px",
										}}
									>
										<span style={{ color: "#15803d", fontWeight: "500" }}>
											✓ Include: {uploadStates.includeList.fileName} (
											{uploadStates.includeList.audienceIds.length} IDs)
										</span>
										<button
											onClick={() => clearUploadedList("includeList")}
											style={{
												background: "none",
												border: "none",
												color: "#dc2626",
												cursor: "pointer",
												padding: "2px",
												borderRadius: "2px",
												display: "flex",
												alignItems: "center",
												fontSize: "12px",
											}}
											title="Remove Include List"
										>
											<RemoveIcon />
										</button>
									</div>
								)}

								{uploadStates.blockedList.isUploaded && (
									<div
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: "4px 6px",
											backgroundColor: "#fee2e2",
											border: "1px solid #dc2626",
											borderRadius: "3px",
											fontSize: "9px",
										}}
									>
										<span style={{ color: "#dc2626", fontWeight: "500" }}>
											✗ Block: {uploadStates.blockedList.fileName} (
											{uploadStates.blockedList.audienceIds.length} IDs)
										</span>
										<button
											onClick={() => clearUploadedList("blockedList")}
											style={{
												background: "none",
												border: "none",
												color: "#dc2626",
												cursor: "pointer",
												padding: "2px",
												borderRadius: "2px",
												display: "flex",
												alignItems: "center",
												fontSize: "12px",
											}}
											title="Remove Block List"
										>
											<RemoveIcon />
										</button>
									</div>
								)}
							</div>
						)}

						{/* Error Messages */}
						{(uploadStates.includeList.error ||
							uploadStates.blockedList.error) && (
							<div
								style={{
									fontSize: "9px",
									color: "#dc2626",
									textAlign: "center",
									backgroundColor: "#fef2f2",
									padding: "4px 6px",
									borderRadius: "3px",
									border: "1px solid #fecaca",
								}}
							>
								{uploadStates.includeList.error ||
									uploadStates.blockedList.error}
							</div>
						)}
					</div>
				</div>

				<style jsx>{`
       .threshold-input-group {
  margin-bottom: 16px;
}

.threshold-input-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.threshold-hint {
  font-size: 9px;
  font-weight: 400;
  color: #6b7280;
  font-style: italic;
  margin-left: 4px;
}

.threshold-input-wrapper {
  position: relative;
}

.threshold-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 11px;
  background: white;
  color: #374151;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.threshold-input:focus {
  outline: none;
  border-color: #0076BD;
  box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.2);
}

.threshold-input:hover {
  border-color: #9ca3af;
}

.threshold-display {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 9px;
  color: #6b7280;
  background: white;
  padding: 2px 4px;
  border-radius: 3px;
  pointer-events: none;
  font-weight: 500;
}

.threshold-inputs-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
        .btn-medium-search {
          background: #059669;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          margin-right: 8px;
        }

        .btn-medium-search:hover:not(:disabled) {
          background: #047857;
        }

        .btn-medium-search:disabled {
          background: #9ca3af;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-refresh {
          background: #f59e0b;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 4px 6px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-refresh:hover:not(:disabled) {
          background: #d97706;
        }

        .btn-refresh:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-medium-primary {
          background: #0076BD;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-medium-primary:hover {
          background: #0056b3;
        }

        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          color: #6b7280;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #1f2937;
        }

        .category-chevron {
          transition: transform 0.2s ease;
          color: #6b7280;
        }

        .category-content {
          padding: 16px;
        }

        .filter-section {
          margin-bottom: 16px;
        }

        .section-content {
          padding: 8px 0;
          contain: layout style;
        }

        /* ✅ FIXED: Multi-select dropdown styles with portal rendering */
        .multi-select-container {
          position: relative;
          margin-bottom: 12px;
        }

        .multi-select-label {
          fontSize: 11px;
          fontWeight: 600;
          marginBottom: 4px;
          display: block;
          color: #374151;
        }

        .multi-select-trigger {
          width: 100%;
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          font-size: 11px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: all 0.2s;
          box-sizing: border-box;
        }

        .multi-select-trigger:hover {
          border-color: #0076BD;
        }

        .multi-select-trigger.open {
          border-color: #0076BD;
          box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.2);
        }

        .multi-select-trigger.loading {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .multi-select-text {
          color: #374151;
          flex: 1;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .multi-select-arrow {
          transition: transform 0.2s ease;
          color: #6b7280;
          margin-left: 8px;
          flex-shrink: 0;
        }

        .multi-select-arrow.open {
          transform: rotate(180deg);
        }

        /* Portal menu styles */
        .multi-select-portal-menu {
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .multi-select-search {
          padding: 8px;
          border-bottom: 1px solid #e5e7eb;
          background: #f8f9fa;
        }

        .multi-select-search-input {
          width: 100%;
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 3px;
          font-size: 11px;
          outline: none;
          box-sizing: border-box;
          background: white;
        }

        .multi-select-search-input:focus {
          border-color: #0076BD;
          box-shadow: 0 0 0 1px rgba(0, 118, 189, 0.2);
        }

        .multi-select-options {
          overflow-y: auto;
        }

        .multi-select-option {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 11px;
          border-bottom: 1px solid #f3f4f6;
          transition: background-color 0.15s;
        }

        .multi-select-option:hover {
          background-color: #f8fafc;
        }

        .multi-select-option:last-child {
          border-bottom: none;
        }

        .multi-select-option.selected {
          background-color: #eff6ff;
          color: #0076BD;
        }

        .multi-select-checkbox {
          margin-right: 8px;
          width: 14px;
          height: 14px;
          flex-shrink: 0;
        }

        .multi-select-option-text {
          flex: 1;
          color: inherit;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .multi-select-loading,
        .multi-select-error,
        .multi-select-no-options {
          padding: 12px;
          text-align: center;
          font-size: 11px;
          color: #6b7280;
        }

        .slider-group {
          display: flex;
          flex-direction: column;
          gap: 16px;
          user-select: none;
          will-change: transform;
        }

        .enhanced-slider-container {
          margin-bottom: 8px;
          user-select: none;
          padding: 0 12px;
          will-change: transform;
        }

        .enhanced-slider-label {
          display: block;
          margin-bottom: 12px;
          font-size: 11px;
          font-weight: 600;
          color: #374151;
          user-select: none;
          pointer-events: none;
        }

        .enhanced-slider-wrapper {
          position: relative;
          padding: 12px 0;
          will-change: transform;
        }

        .enhanced-slider-track {
          position: relative;
          width: 100%;
          height: 3px;
          background: #e5e7eb;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.15s ease;
          outline: none;
          overflow: visible;
          padding: 8px 0;
          margin: -8px 0;
          user-select: none;
          touch-action: none;
          will-change: transform, background-color;
        }

        .enhanced-slider-track:hover {
          background: #d1d5db;
          box-shadow: 0 2px 4px rgba(0, 118, 189, 0.2);
        }

        .enhanced-slider-track:focus {
          box-shadow: 0 0 0 3px rgba(0, 118, 189, 0.3);
          outline: none;
        }

        .enhanced-slider-track.dragging {
          cursor: grabbing !important;
          box-shadow: 0 4px 8px rgba(0, 118, 189, 0.3);
          user-select: none !important;
          background: #d1d5db;
        }

        .enhanced-slider-fill {
          position: absolute;
          left: 0;
          top: 8px;
          height: 6px;
          background: linear-gradient(135deg, #0076BD 0%, #0056b3 100%);
          border-radius: 4px;
          transition: none;
          pointer-events: none;
          user-select: none;
          max-width: 100%;
          will-change: width;
        }

        .enhanced-slider-thumb {
          position: absolute;
          top: 50%;
          width: 18px;
          height: 18px;
          background: #0076BD;
          border: 3px solid white;
          border-radius: 50%;
          cursor: grab;
          transform: translate(-50%, -50%);
          transition: box-shadow 0.15s ease, background-color 0.15s ease;
          box-shadow: 0 2px 6px rgba(0, 118, 189, 0.4);
          z-index: 10;
          user-select: none;
          pointer-events: auto;
          will-change: transform, background-color;
        }

        .enhanced-slider-thumb:hover {
          background: #0056b3;
          transform: translate(-50%, -50%) scale(1.1);
          box-shadow: 0 3px 8px rgba(0, 118, 189, 0.5);
        }

        .enhanced-slider-thumb.dragging {
          cursor: grabbing !important;
          background: #003d82;
          transform: translate(-50%, -50%) scale(1.15);
          box-shadow: 0 4px 12px rgba(0, 118, 189, 0.6);
          transition: none !important;
        }

        .enhanced-slider-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          font-size: 10px;
          color: #6b7280;
          font-weight: 500;
          user-select: none;
          pointer-events: none;
        }

        .sub-section-header:hover {
          background-color: #f1f5f9;
        }

        @media (max-width: 768px) {
          .filters-sidebar {
            width: 100%;
            position: fixed;
            top: 0;
            left: 0;
            z-index: 9999;
          }

          .enhanced-slider-thumb {
            width: 22px;
            height: 22px;
            border-width: 3px;
          }
          
          .enhanced-slider-track {
            height: 8px;
            padding: 12px 0;
            margin: -12px 0;
          }
          
          .enhanced-slider-fill {
            top: 12px;
            height: 8px;
          }
          
          .enhanced-slider-wrapper {
            padding: 15px 0;
          }
        }
      `}</style>
			</div>
		);
	},
);

export default AudienceFiltersSidebar;
